import { useState } from 'react';
import { devLog } from '@/utils/logger';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import {
  Download,
  Printer,
  Share,
  Mail,
  FileText,
  MoreVertical,
  Copy,
  Eye,
  ExternalLink,
  CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import type { PayslipWithLineItems, Payslip } from '@/services/payslips/types';

interface PayslipActionsMenuProps {
  payslip: PayslipWithLineItems | Payslip;
  onExport?: (format: 'pdf' | 'csv' | 'json') => void;
  onPrint?: () => void;
  onShare?: () => void;
  onEmail?: (recipient?: string) => void;
  className?: string;
}

export function PayslipActionsMenu({
  payslip,
  onExport,
  onPrint,
  onShare,
  onEmail,
  className
}: PayslipActionsMenuProps) {
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleExport = (format: 'pdf' | 'csv' | 'json') => {
    if (onExport) {
      onExport(format);
    } else {
      // Default export behavior
      devLog(`Exporting payslip ${payslip.id} as ${format}`);
      toast.success(`Payslip export started (${format.toUpperCase()})`);
    }
  };

  const handlePrint = () => {
    if (onPrint) {
      onPrint();
    } else {
      // Default print behavior
      window.print();
      toast.success('Print dialog opened');
    }
  };

  const handleCopyLink = async () => {
    try {
      const url = `${window.location.origin}/payslips/${payslip.id}`;
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Payslip link copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `Payslip - ${new Date(payslip.start_date).toLocaleDateString()}`,
        text: 'View my payslip details',
        url: `${window.location.origin}/payslips/${payslip.id}`
      });
    } else {
      setShowShareDialog(true);
    }
  };

  const handleEmail = () => {
    if (onEmail) {
      onEmail();
    } else {
      // Default email behavior
      const subject = encodeURIComponent(`Payslip - ${new Date(payslip.start_date).toLocaleDateString()}`);
      const body = encodeURIComponent(`Please find my payslip details: ${window.location.origin}/payslips/${payslip.id}`);
      window.location.href = `mailto:?subject=${subject}&body=${body}`;
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className={className}>
            <MoreVertical className="h-4 w-4" />
            Actions
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>Payslip Actions</DropdownMenuLabel>
          <DropdownMenuSeparator />
          
          {/* Export Options */}
          <DropdownMenuItem onClick={() => handleExport('pdf')}>
            <FileText className="mr-2 h-4 w-4" />
            <span>Export as PDF</span>
            <Badge variant="outline" className="ml-auto text-xs">
              Recommended
            </Badge>
          </DropdownMenuItem>
          
          <DropdownMenuItem onClick={() => handleExport('csv')}>
            <Download className="mr-2 h-4 w-4" />
            <span>Export as CSV</span>
          </DropdownMenuItem>
          
          <DropdownMenuItem onClick={() => handleExport('json')}>
            <Download className="mr-2 h-4 w-4" />
            <span>Export as JSON</span>
            <Badge variant="secondary" className="ml-auto text-xs">
              Data
            </Badge>
          </DropdownMenuItem>
          
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Share Dialog for browsers without native sharing */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Share Payslip</DialogTitle>
            <DialogDescription>
              Share this payslip with others using the options below.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <div className="grid flex-1 gap-2">
                <label htmlFor="link" className="sr-only">
                  Payslip Link
                </label>
                <input
                  id="link"
                  className="w-full px-3 py-2 border rounded-md text-sm bg-muted"
                  readOnly
                  value={`${window.location.origin}/payslips/${payslip.id}`}
                />
              </div>
              <Button type="button" size="sm" className="px-3" onClick={handleCopyLink}>
                <span className="sr-only">Copy</span>
                {copied ? (
                  <CheckCircle className="h-4 w-4 text-green-600" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
            
            <div className="grid grid-cols-2 gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleEmail}
                className="flex items-center gap-2"
              >
                <Mail className="h-4 w-4" />
                Email
              </Button>
              
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => {
                  handleExport('pdf');
                  setShowShareDialog(false);
                }}
                className="flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                Download PDF
              </Button>
            </div>
          </div>
          
          <DialogFooter className="sm:justify-start">
            <Button 
              type="button" 
              variant="secondary" 
              onClick={() => setShowShareDialog(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}